import React, { useState, useEffect } from 'react';
import axiosInstance from '../../lib/axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Package,
  Filter,
  Copy,
  Upload,
  Store
} from 'lucide-react';
import { formatPrice } from '../../lib/utils';
import LoadingSpinner from '../../components/LoadingSpinner';
import { toast } from 'sonner';
import ProductFormDialog from '../../components/admin/ProductFormDialog';
import ProductViewDialog from '../../components/admin/ProductViewDialog';
import BulkUploadDialog from '../../components/admin/BulkUploadDialog';

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showBulkUploadDialog, setShowBulkUploadDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchVendors();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      // Admin should see all products regardless of approval status
      const response = await axiosInstance.get(`${API_URL}/api/admin/products/all`);
      // Normalize IDs for all products
      const productsWithId = response.data.map(p => ({
        ...p,
        id: p.id || p._id
      }));
      setProducts(productsWithId);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get(`${API_URL}/api/collections`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch collections:', error);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axiosInstance.get(`${API_URL}/api/admin/vendors`);
      setVendors(response.data || []);
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    }
  };

  const getVendorName = (vendorId) => {
    if (!Array.isArray(vendors)) return 'Unknown Vendor';
    const vendor = vendors.find(v => (v.id || v._id) === vendorId);
    return vendor?.business_name || 'Unknown Vendor';
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = !searchQuery || 
      (product.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (product.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (product.sku || '').toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || 
      (filterStatus === 'active' && product.is_active) ||
      (filterStatus === 'inactive' && !product.is_active) ||
      (filterStatus === 'low-stock' && product.inventory < 10);
    
    const matchesVendor = vendorFilter === 'all' || product.vendor_id === vendorFilter;
    
    return matchesSearch && matchesFilter && matchesVendor;
  });

  // Count products per vendor
  const vendorProductCounts = Array.isArray(vendors) ? vendors.reduce((acc, vendor) => {
    const vendorId = vendor.id || vendor._id;
    acc[vendorId] = products.filter(p => p.vendor_id === vendorId).length;
    return acc;
  }, {}) : {};

  const handleDeleteProduct = async (productId) => {
    const actualId = productId || selectedProduct?.id || selectedProduct?._id;
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await axiosInstance.delete(`${API_URL}/api/products/${actualId}`);
        setProducts(products.filter(p => (p.id || p._id) !== actualId));
        toast.success('Product deleted successfully');
      } catch (error) {
        console.error('Failed to delete product:', error);
        toast.error(error.response?.data?.detail || 'Failed to delete product');
      }
    }
  };

  const handleAddProduct = () => {
    setSelectedProduct(null);
    setShowFormDialog(true);
  };

  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setShowFormDialog(true);
  };

  const handleCloneProduct = (product) => {
    // Create a copy of the product with modifications for cloning
    const clonedProduct = {
      ...product,
      id: null, // Remove ID so it creates a new product
      _id: null, // Remove MongoDB ID
      name: `${product.name} (Copy)`, // Add "(Copy)" to the name
      sku: product.sku ? `${product.sku}-COPY` : '', // Modify SKU to avoid duplicates
      is_active: false, // Set as inactive by default for review
      variants: product.variants ? product.variants.map(variant => ({
        ...variant,
        sku: variant.sku ? `${variant.sku}-COPY` : ''
      })) : []
    };
    
    setSelectedProduct(clonedProduct);
    setShowFormDialog(true);
    toast.success('Product cloned! Review and save to create a copy.');
  };

  const handleViewProduct = (product) => {
    setSelectedProduct(product);
    setShowViewDialog(true);
  };

  const handleSaveProduct = async (productData) => {
    try {
      const productId = selectedProduct?.id || selectedProduct?._id;
      if (selectedProduct) {
        // Update existing product
        await axiosInstance.put(`${API_URL}/api/products/${productId}`, productData);
        toast.success('Product updated successfully');
      } else {
        // Create new product
        await axiosInstance.post(`${API_URL}/api/products`, productData);
        toast.success('Product created successfully');
      }
      // Refresh the product list
      await fetchProducts();
    } catch (error) {
      console.error('Save product error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to save product';
      toast.error(errorMsg);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Products Management</h1>
              <p className="text-gray-600 mt-1 sm:mt-2 text-sm sm:text-base">
                Manage your product catalog
              </p>
            </div>
            
            <div className="flex gap-2 w-full sm:w-auto">
              <Button 
                variant="outline" 
                className="flex-1 sm:flex-initial gap-2"
                onClick={async () => {
                  try {
                    toast.loading('Generating SEO slugs...');
                    await axiosInstance.post(`${API_URL}/api/admin/generate-slugs`);
                    toast.dismiss();
                    toast.success('SEO slugs generated for all products!');
                    fetchProducts();
                  } catch (error) {
                    toast.dismiss();
                    toast.error('Failed to generate slugs');
                  }
                }}
              >
                Generate SEO URLs
              </Button>
              <Button 
                variant="outline" 
                className="flex-1 sm:flex-initial gap-2" 
                data-testid="bulk-upload-button" 
                onClick={() => setShowBulkUploadDialog(true)}
              >
                <Upload className="w-4 h-4" />
                <span className="hidden sm:inline">Bulk Upload</span>
                <span className="sm:hidden">Bulk</span>
              </Button>
              <Button className="btn-primary flex-1 sm:flex-initial" data-testid="add-product-button" onClick={handleAddProduct}>
                <Plus className="w-4 h-4 mr-2" />
                Add Product
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="products-search-input"
            />
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Vendor Filter */}
            <Select value={vendorFilter} onValueChange={setVendorFilter}>
              <SelectTrigger className="w-56">
                <Store className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by vendor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Vendors ({products.length})</SelectItem>
                {Array.isArray(vendors) && vendors.map((vendor) => {
                  const vendorId = vendor.id || vendor._id;
                  return (
                    <SelectItem key={vendorId} value={vendorId}>
                      {vendor.business_name} ({vendorProductCounts[vendorId] || 0})
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="products-filter-select"
            >
              <option value="all">All Products</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="low-stock">Low Stock</option>
            </select>
          </div>
        </div>

        {/* Products Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Products</p>
                  <p className="text-2xl font-bold text-gray-900" data-testid="total-products-count">
                    {products.length}
                  </p>
                </div>
                <Package className="w-8 h-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Products</p>
                  <p className="text-2xl font-bold text-green-600">
                    {products.filter(p => p.is_active).length}
                  </p>
                </div>
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Low Stock</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {products.filter(p => p.inventory < 10).length}
                  </p>
                </div>
                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Out of Stock</p>
                  <p className="text-2xl font-bold text-red-600">
                    {products.filter(p => p.inventory === 0).length}
                  </p>
                </div>
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Products Table/Cards */}
        <Card>
          <CardHeader>
            <CardTitle>Products ({filteredProducts.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredProducts.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No products found</h3>
                <p className="text-gray-600">Try adjusting your search or filter criteria.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {filteredProducts.map((product) => (
                  <Card key={product.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex flex-col sm:flex-row gap-4">
                        {/* Product Image and Info */}
                        <div className="flex gap-4 flex-1 min-w-0">
                          <img 
                            src={product.images[0] || 'https://via.placeholder.com/80x80/E8F5E9/2E7D32?text=No+Image'} 
                            alt={product.name}
                            className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 truncate">{product.name}</h3>
                            <p className="text-sm text-gray-500 line-clamp-2 mb-2">{product.description}</p>
                            <div className="flex flex-wrap gap-2 text-sm">
                              <span className="bg-gray-100 px-2 py-1 rounded">SKU: {product.sku}</span>
                              <span className="font-semibold text-green-700">{formatPrice(product.sale_price || product.price)}</span>
                              {product.sale_price && (
                                <span className="text-gray-500 line-through">{formatPrice(product.price)}</span>
                              )}
                            </div>
                            {/* Vendor Badge */}
                            <div className="mt-2">
                              <Badge variant="outline" className="text-xs">
                                <Store className="w-3 h-3 mr-1" />
                                {getVendorName(product.vendor_id)}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        
                        {/* Stock and Status */}
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <p className="text-xs text-gray-600">Stock</p>
                            <p className={`font-semibold ${product.inventory === 0 ? 'text-red-600' : 'text-gray-900'}`}>
                              {product.inventory}
                            </p>
                          </div>
                          <div>
                            <Badge className={product.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                              {product.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex sm:flex-col gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedProduct(product);
                              setShowViewDialog(true);
                            }}
                            className="flex-1 sm:flex-initial"
                          >
                            <Eye className="w-4 h-4 sm:mr-1" />
                            <span className="hidden sm:inline">View</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCloneProduct(product)}
                            className="flex-1 sm:flex-initial text-blue-600 hover:bg-blue-50"
                            title="Clone Product"
                          >
                            <Copy className="w-4 h-4 sm:mr-1" />
                            <span className="hidden sm:inline">Clone</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedProduct(product);
                              setShowFormDialog(true);
                            }}
                            className="flex-1 sm:flex-initial"
                          >
                            <Edit className="w-4 h-4 sm:mr-1" />
                            <span className="hidden sm:inline">Edit</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteProduct(product.id)}
                            className="text-red-600 hover:bg-red-50 flex-1 sm:flex-initial"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Product Form Dialog */}
      <ProductFormDialog
        open={showFormDialog}
        onClose={() => setShowFormDialog(false)}
        product={selectedProduct}
        onSave={handleSaveProduct}
        categories={categories}
      />

      {/* Product View Dialog */}
      <ProductViewDialog
        open={showViewDialog}
        onClose={() => setShowViewDialog(false)}
        product={selectedProduct}
      />

      {/* Bulk Upload Dialog */}
      <BulkUploadDialog
        open={showBulkUploadDialog}
        onClose={() => setShowBulkUploadDialog(false)}
        onSuccess={fetchProducts}
      />
    </div>
  );
}
